import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ApiError } from '@/api'
import { useAuth } from '@/contexts/AuthContext'
import { routes } from '@/routes'
import { AuthCard } from '@/components/auth/AuthCard'
import { RedirectingNotice } from '@/components/auth/RedirectingNotice'
import { Turnstile } from '@/components/auth/Turnstile'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'

export function RegisterPage() {
  const navigate = useNavigate()
  const { register } = useAuth()

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [turnstileToken, setTurnstileToken] = useState<string>()
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [status, setStatus] = useState<'idle' | 'submitting' | 'redirecting'>('idle')

  function update(field: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setFieldErrors({})

    // Confere a confirmação antes de gastar uma ida ao servidor.
    if (form.password !== form.confirmPassword) {
      setFieldErrors({ confirmPassword: 'As senhas não são iguais.' })
      return
    }

    setStatus('submitting')
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        turnstileToken,
      })
      setStatus('redirecting')
      navigate(routes.home, { replace: true })
    } catch (err) {
      setStatus('idle')
      if (err instanceof ApiError) {
        setError(err.message)
        setFieldErrors(err.fieldErrors ?? {})
      } else {
        setError('Não foi possível criar a conta. Tente novamente.')
      }
    }
  }

  return (
    <AuthCard title="Criar uma nova conta">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {error && <Alert tone="danger">{error}</Alert>}

        <TextField
          label="Nome"
          name="name"
          autoComplete="name"
          required
          value={form.name}
          onChange={(event) => update('name', event.target.value)}
          error={fieldErrors.name}
        />

        <TextField
          label="E-mail"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={form.email}
          onChange={(event) => update('email', event.target.value)}
          error={fieldErrors.email}
        />

        <TextField
          label="Senha"
          type="password"
          name="new-password"
          autoComplete="new-password"
          required
          minLength={6}
          value={form.password}
          onChange={(event) => update('password', event.target.value)}
          error={fieldErrors.password}
        />

        <TextField
          label="Confirmar senha"
          type="password"
          autoComplete="new-password"
          required
          value={form.confirmPassword}
          onChange={(event) => update('confirmPassword', event.target.value)}
          error={fieldErrors.confirmPassword}
        />

        <Turnstile onVerify={setTurnstileToken} onExpire={() => setTurnstileToken(undefined)} />

        {status === 'redirecting' ? (
          <RedirectingNotice />
        ) : (
          <Button type="submit" size="lg" block loading={status === 'submitting'}>
            Criar conta
          </Button>
        )}

        <Link
          to={routes.login}
          className="py-1 text-center text-sm text-ink-soft transition-colors hover:text-ink"
        >
          Cancelar
        </Link>
      </form>
    </AuthCard>
  )
}
